import { useId } from 'react'

// One choice from a few, as a row of pills: plainer than a dropdown, and easy to tap on a phone.
export function Choice({
  legend,
  hint,
  value,
  options,
  onChange,
}: {
  legend: string
  hint?: string
  value: string
  options: [string, string][]
  onChange: (value: string) => void
}) {
  const name = useId()
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-2 text-sm font-medium">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map(([v, text]) => (
          <label
            key={v}
            className="flex min-h-11 cursor-pointer items-center rounded-full border border-input px-4 text-sm transition-colors has-checked:border-brand has-checked:bg-brand has-checked:text-brand-foreground has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50"
          >
            <input
              type="radio"
              name={name}
              value={v}
              checked={value === v}
              onChange={() => onChange(v)}
              className="sr-only"
            />
            {text}
          </label>
        ))}
      </div>
      {hint && <p className="text-sub text-muted-foreground">{hint}</p>}
    </fieldset>
  )
}

// Any number of choices from a few, as a row of pills that toggle.
export function Choices<T extends string>({
  legend,
  hint,
  value,
  options,
  onChange,
}: {
  legend: string
  hint?: string
  value: readonly T[]
  options: [T, string][]
  onChange: (value: T[]) => void
}) {
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-2 text-sm font-medium">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map(([v, text]) => (
          <label
            key={v}
            className="flex min-h-11 cursor-pointer items-center rounded-full border border-input px-4 text-sm transition-colors has-checked:border-brand has-checked:bg-brand has-checked:text-brand-foreground has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50"
          >
            <input
              type="checkbox"
              value={v}
              checked={value.includes(v)}
              onChange={(e) => onChange(e.target.checked ? [...value, v] : value.filter((x) => x !== v))}
              className="sr-only"
            />
            {text}
          </label>
        ))}
      </div>
      {hint && <p className="text-sub text-muted-foreground">{hint}</p>}
    </fieldset>
  )
}
